import { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import axios from "axios";
import {
  MdMyLocation,
  MdMap,
  MdPlace,
  MdRestaurant,
  MdLocationOn,
  MdClose,
  MdPeople,
  MdImageNotSupported,
  MdCheckCircle,
  MdChevronLeft,
  MdChevronRight,
  MdPets,
  MdDirectionsBike,
} from "react-icons/md";
import "./NearByRestaurants.css";
import heroBanner from "../../Assets/Image/image 30.png";
import Footer from "../../components/Footer/Footer";
import Navbar from "../Navbar/Navbar";

// --- Constants ---
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const API_BASE_URL = process.env.REACT_APP_SECRET;
const LIBRARIES = ["places"];
const FIXED_RADIUS = 8047;

const NearByRestaurants = () => {
  // 2. Initialize navigate hook
  const navigate = useNavigate();

  // --- Existing State ---
  const [searchMode, setSearchMode] = useState("restaurant");
  const [locationQuery, setLocationQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState("");
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState(null);
  const [blur, setBlur] = useState(false);

  // Map State
  const [mapMarker, setMapMarker] = useState(null);
  const [mapSearchInput, setMapSearchInput] = useState("");
  const [mapCenter, setMapCenter] = useState({ lat: 51.5074, lng: -0.1278 });

  // Slider State (Restaurant Cards)
  const [sliderIndices, setSliderIndices] = useState({});

  // --- Table Selection Modal State ---
  const [isTableModalOpen, setIsTableModalOpen] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [tableError, setTableError] = useState(null);

  // Track the specific table selected by the user before confirmation
  const [selectedTable, setSelectedTable] = useState(null);

  // New State for Table Image Carousel
  const [tableImageIndex, setTableImageIndex] = useState(0);

  // --- NEW STATE FOR USER LOCATION ---
  const [userLocation, setUserLocation] = useState({
    lat: null,
    lng: null,
  });

  const geocoderRef = useRef(null);
  const mapRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchContainerRef = useRef(null);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  const isMapsReady =
    isLoaded && !loadError && typeof window.google !== "undefined";

  // --- Effects ---
  useEffect(() => {
    if (isMapsReady && window.google) {
      autocompleteServiceRef.current =
        new window.google.maps.places.AutocompleteService();
      geocoderRef.current = new window.google.maps.Geocoder();
    }
  }, [isMapsReady]);

  // --- NEW EFFECT: Fetch User Location on Mount ---
  useEffect(() => {
    if (!navigator.geolocation) {
      console.warn("Geolocation not supported");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      (err) => {
        console.error("Geo permission denied or error:", err);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      },
    );
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset table selection state when modal opens/closes or restaurant changes
  useEffect(() => {
    if (!isTableModalOpen) {
      setSelectedTable(null);
      setTableImageIndex(0); // Reset image carousel
    }
  }, [isTableModalOpen]);

  // Reset image index when selected table changes
  useEffect(() => {
    setTableImageIndex(0);
  }, [selectedTable?.id]);

  // --- Helpers ---
  const handleSlideChange = (e, id, totalSlides, direction) => {
    e.preventDefault();
    e.stopPropagation();
    setSliderIndices((prev) => {
      const current = prev[id] || 0;
      let next = direction === "next" ? current + 1 : current - 1;
      if (next < 0) next = totalSlides - 1;
      if (next >= totalSlides) next = 0;
      return { ...prev, [id]: next };
    });
  };

  // Helper for Table Image Carousel
  const handleTableImageSlide = (direction) => {
    if (!selectedTable) return;
    const images = selectedTable.imageUrls || [];
    if (images.length <= 1) return;

    setTableImageIndex((prev) => {
      let next = direction === "next" ? prev + 1 : prev - 1;
      if (next < 0) next = images.length - 1;
      if (next >= images.length) next = 0;
      return next;
    });
  };

  // --- Fetch Table Data ---
  const fetchTableData = useCallback(async (restaurantId) => {
    if (!API_BASE_URL || !restaurantId) return;

    setTableLoading(true);
    setTableError(null);
    setTableData([]);
    setSelectedTable(null);
    setTableImageIndex(0);

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/RestaurantTable/GetAllRestaurantTableInfosByRestaurantId`,
        { params: { restaurantId } },
      );

      if (Array.isArray(response.data)) {
        const activeTables = response.data
          .filter((table) => table.isActive)
          .map((table) => {
            // Process image paths into an array for the carousel
            let imageUrls = [];
            if (table.tableImagePath) {
              // Assuming tableImagePath might be comma separated or single URL
              imageUrls = table.tableImagePath
                .split(",")
                .map((url) => url.trim())
                .filter((url) => url);
            }

            return {
              id: table.tableId,
              name: table.tableName || `Table ${table.serailNumber + 1}`,
              description: table.description,
              imageUrls: imageUrls, // Changed from imageUrl to imageUrls array
              capacity: table.totalChairUnderThisTable,
              qrCode: table.tableQRCode,
              floor: table.floorNumber,
            };
          });

        setTableData(activeTables);
        // Auto-select first table if available for better UX in new layout
        if (activeTables.length > 0) {
          setSelectedTable(activeTables[0]);
        }
      } else {
        setTableData([]);
      }
    } catch (err) {
      console.error("Table fetch error:", err);
      setTableError("Unable to load table information. Please try again.");
    } finally {
      setTableLoading(false);
    }
  }, []);

  // --- Handle Card Click (Open Modal) ---
  const handleCardClick = useCallback(
    (restaurant) => {
      setSelectedRestaurant(restaurant);
      setIsTableModalOpen(true);
      fetchTableData(restaurant.id);
    },
    [fetchTableData],
  );

  // --- Handle Table Selection (Visual Only) ---
  const handleTableSelect = (table) => {
    setSelectedTable(table);
    setTableImageIndex(0); // Reset carousel to first image on new selection
  };

  // --- Handle Final Confirmation ---
  const handleConfirmBooking = () => {
    if (!selectedTable || !selectedRestaurant) return;

    const tableData = {
      restaurantId: selectedRestaurant.id,
      tableId: selectedTable.id,
      tableName: selectedTable.name,
    };

    sessionStorage.setItem("tableData", JSON.stringify(tableData));

    if (selectedRestaurant.urlName) {
      navigate(`/${selectedRestaurant.urlName}`);
    } else {
      console.error("Restaurant URL Name is missing");
      setIsTableModalOpen(false);
    }
  };

  // --- API Logic: Search by Restaurant Name ---
  const searchRestaurantsByName = useCallback(
    async (query) => {
      if (!API_BASE_URL) {
        setError("Configuration error: API Base URL missing.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setHasSearched(true);
      setError(null);
      setIsMapOpen(false);

      try {
        // Prepare parameters
        const params = { searchStr: query };

        // Add user location if available
        if (userLocation.lat && userLocation.lng) {
          params.userLat = userLocation.lat;
          params.userLong = userLocation.lng;
        }

        // UPDATED ENDPOINT
        const response = await axios.get(
          `${API_BASE_URL}/api/Search/GetSearchDataByName`,
          { params },
        );

        const data = response.data;

        // Handle both Array response (from your screenshot) and Object response
        let rawRestaurants = [];
        if (Array.isArray(data)) {
          rawRestaurants = data;
        } else if (data && data.searchRestaurantView) {
          rawRestaurants = data.searchRestaurantView;
        }

        if (Array.isArray(rawRestaurants) && rawRestaurants.length > 0) {
          const mappedRestaurants = rawRestaurants.map((item) => ({
            id: item.id,
            name: item.name,
            category: item.restaurantCategories,
            urlName: item.urlName,
            vicinity:
              item.restaurantDetails ||
              item.restaurantCategories ||
              "Location available",
            isPetFriendly: item.isPetFriendly || false,
            logoUrl: item.restaurantLogo,
            priceLevel: 2,
            isOpen: true,
            // Convert meters to miles if provided, otherwise "N/A"
            distance: item.distanceInMeters
              ? (item.distanceInMeters / 1609.34).toFixed(2) + " mi"
              : "N/A",
            placeId: item.urlName,
            backgroundImageUrl: [],
          }));
          setRestaurants(mappedRestaurants);
        } else {
          setRestaurants([]);
          setError("No restaurants found matching that name.");
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setError("Unable to connect to our servers. Please try again later.");
        setRestaurants([]);
      } finally {
        setLoading(false);
      }
    },
    [userLocation.lat, userLocation.lng],
  );

  // --- API Logic: Search by Location ---
  const searchNearbyRestaurants = useCallback(async (lat, lng) => {
    if (!API_BASE_URL) {
      setError("Configuration error: API Base URL missing.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setHasSearched(true);
    setError(null);
    setIsMapOpen(false);

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/Restaurant/GetNearByRestaurants`,
        { params: { latitude: lat, longitude: lng, radius: FIXED_RADIUS } },
      );
      const data = response.data;
      if (Array.isArray(data)) {
        const mappedRestaurants = data.map((item) => ({
          id: item.id,
          name: item.name || "Unknown Restaurant",
          urlName: item.urlName || "",
          vicinity:
            [item.addressLine1, item.addressLine2, item.postCode]
              .filter(Boolean)
              .join(", ") || "Location available",
          rating: item.hygieneRating || "N/A",
          isPetFriendly: item.isPetFriendly || false,
          userRatingsTotal: null,
          logoUrl: item.logoUrl,
          priceLevel: 2,
          isOpen: !item.isOutOfHour,
          distance: item.distanceInMiles,
          placeId: item.id,
          backgroundImageUrl: [item.imagePaths, item.premisesImageUrl]
            .filter(Boolean)
            .join(",")
            .split(","),
        }));
        setRestaurants(mappedRestaurants);
      } else {
        setRestaurants([]);
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setError("Unable to connect to our servers. Please try again later.");
      setRestaurants([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleLocationSelect = useCallback(
    (lat, lng, address) => {
      setSelectedAddress(address);
      setLocationQuery(address);
      searchNearbyRestaurants(lat, lng);
    },
    [searchNearbyRestaurants],
  );

  const handleUseMyLocation = useCallback(() => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        if (isMapsReady) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode(
            { location: { lat: latitude, lng: longitude } },
            (results, status) => {
              const addr =
                status === "OK" && results?.[0]
                  ? results[0].formatted_address
                  : `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;

              setMapMarker({ lat: latitude, lng: longitude });
              setMapCenter({ lat: latitude, lng: longitude });
              setSelectedAddress(addr);
              setMapSearchInput(addr);
              setIsMapOpen(true);
              setLoading(false);
            },
          );
        } else {
          handleLocationSelect(latitude, longitude, "Current Location");
        }
      },
      () => {
        setLoading(false);
        setError(
          "Location access denied. Please enable permissions or search manually.",
        );
      },
    );
  }, [isMapsReady, handleLocationSelect]);

  // --- Fetch Predictions ---
  const fetchPredictions = useCallback(
    (input) => {
      if (!input) {
        setSuggestions([]);
        setIsDropdownOpen(false);
        return;
      }

      if (searchMode === "location") {
        if (!autocompleteServiceRef.current || !isMapsReady) {
          setSuggestions([]);
          setIsDropdownOpen(false);
          return;
        }
        autocompleteServiceRef.current.getPlacePredictions(
          { input },
          (predictions, status) => {
            if (
              status === window.google.maps.places.PlacesServiceStatus.OK &&
              predictions
            ) {
              setSuggestions(predictions);
              setIsDropdownOpen(true);
            } else {
              setSuggestions([]);
              setIsDropdownOpen(false);
            }
          },
        );
      } else {
        // UPDATED: Use GetSearchDataByName with user location
        const params = { searchStr: input };
        if (userLocation.lat && userLocation.lng) {
          params.userLat = userLocation.lat;
          params.userLong = userLocation.lng;
        }

        axios
          .get(`${API_BASE_URL}/api/Search/GetSearchDataByName`, { params })
          .then((response) => {
            const data = response.data;

            // Handle both Array response and Object response
            let restaurantResults = [];
            if (Array.isArray(data)) {
              restaurantResults = data;
            } else if (data && data.searchRestaurantView) {
              restaurantResults = data.searchRestaurantView;
            }

            const formattedSuggestions = restaurantResults
              .slice(0, 6)
              .map((r) => ({
                id: r.id,
                place_id: r.urlName,
                description: r.name,
                structured_formatting: {
                  main_text: r.name,
                  secondary_text: r.restaurantCategories || "",
                },
                isBackendResult: true,
                type: "restaurant",
                logoUrl: r.restaurantLogo,
                urlName: r.urlName,
              }));
            setSuggestions(formattedSuggestions);
            setIsDropdownOpen(true);
          })
          .catch(() => {
            setSuggestions([]);
            setIsDropdownOpen(false);
          });
      }
    },
    [searchMode, isMapsReady, userLocation.lat, userLocation.lng],
  );

  const handleInputChange = (e) => {
    const value = e.target.value;
    setLocationQuery(value);
    setTimeout(() => {
      if (value.length > 2) {
        fetchPredictions(value);
      } else {
        setSuggestions([]);
        setIsDropdownOpen(false);
      }
    }, 300);
  };

  // --- MODIFIED FUNCTION START ---
  const handleSuggestionClick = (prediction) => {
    setLoading(true);

    // Check if this is a restaurant result from our backend API
    if (prediction.isBackendResult && prediction.type === "restaurant") {
      // 1. Update the input field to show the selected name
      setLocationQuery(prediction.description);

      // 2. Close the dropdown
      setIsDropdownOpen(false);

      // 3. Construct a restaurant object that matches the structure
      //    expected by handleCardClick and fetchTableData
      const selectedRestaurantObj = {
        id: prediction.id,
        name: prediction.description,
        urlName: prediction.urlName,
        logoUrl: prediction.logoUrl,
        vicinity:
          prediction.structured_formatting?.secondary_text ||
          "Location available",
        isOpen: true,
      };

      // 4. Directly trigger the modal logic
      // This calls fetchTableData internally and sets isTableModalOpen to true
      handleCardClick(selectedRestaurantObj);

      // 5. Stop loading state as we are initiating the specific fetch now
      setLoading(false);
    } else if (searchMode === "location") {
      // --- Existing Logic for Location Search (Google Maps) ---
      if (!isMapsReady) {
        setLoading(false);
        return;
      }
      const placesService = new window.google.maps.places.PlacesService(
        document.createElement("div"),
      );
      placesService.getDetails(
        {
          placeId: prediction.place_id,
          fields: ["geometry", "formatted_address"],
        },
        (result, status) => {
          setLoading(false);
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            result.geometry
          ) {
            const lat = result.geometry.location.lat();
            const lng = result.geometry.location.lng();
            setMapMarker({ lat, lng });
            setMapCenter({ lat, lng });
            setSelectedAddress(result.formatted_address);
            setMapSearchInput(result.formatted_address);
            setIsMapOpen(true);
            setSuggestions([]);
            setIsDropdownOpen(false);
          } else {
            setError("Could not retrieve details for this location.");
          }
        },
      );
    } else {
      // Fallback for safety
      setLoading(false);
    }
  };
  // --- MODIFIED FUNCTION END ---

  const handleManualSearch = useCallback(() => {
    if (!locationQuery.trim()) return;
    setLoading(true);
    setError(null);
    if (searchMode === "restaurant") {
      searchRestaurantsByName(locationQuery);
      setIsDropdownOpen(false);
    } else {
      if (isMapsReady) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: locationQuery }, (results, status) => {
          if (status === "OK" && results[0]) {
            const lat = results[0].geometry.location.lat();
            const lng = results[0].geometry.location.lng();
            setMapMarker({ lat, lng });
            setMapCenter({ lat, lng });
            setSelectedAddress(results[0].formatted_address);
            setMapSearchInput(locationQuery);
            setIsMapOpen(true);
            setLoading(false);
            setSuggestions([]);
            setIsDropdownOpen(false);
          } else {
            setLoading(false);
            setError(
              "Could not find that location. Please check spelling or try a postcode.",
            );
          }
        });
      } else {
        setLoading(false);
        setError("Map services are loading. Please wait a moment.");
      }
    }
  }, [locationQuery, isMapsReady, searchMode, searchRestaurantsByName]);

  // --- Map Logic ---
  const onMapLoad = useCallback(
    (map) => {
      mapRef.current = map;
      if (isMapsReady) geocoderRef.current = new window.google.maps.Geocoder();
    },
    [isMapsReady],
  );

  const reverseGeocode = useCallback((lat, lng) => {
    if (!geocoderRef.current) {
      setSelectedAddress(`${lat.toFixed(4)}, ${lng.toFixed(4)}`);
      return;
    }
    geocoderRef.current.geocode(
      { location: { lat, lng } },
      (results, status) => {
        if (status === "OK" && results?.[0]) {
          setSelectedAddress(results[0].formatted_address);
        } else {
          setSelectedAddress(`${lat.toFixed(4)}, ${lng.toFixed(4)}`);
        }
      },
    );
  }, []);

  const handleMapClick = useCallback(
    (e) => {
      if (!e.latLng) return;
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setMapMarker({ lat, lng });
      reverseGeocode(lat, lng);
    },
    [reverseGeocode],
  );

  const handleMarkerDragEnd = useCallback(
    (e) => {
      if (!e.latLng) return;
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setMapMarker({ lat, lng });
      reverseGeocode(lat, lng);
    },
    [reverseGeocode],
  );

  const handleMapSearch = useCallback(() => {
    if (!geocoderRef.current || !mapSearchInput.trim()) return;
    geocoderRef.current.geocode(
      { address: mapSearchInput },
      (results, status) => {
        if (status === "OK" && results?.[0]) {
          const loc = results[0].geometry.location;
          const lat = loc.lat();
          const lng = loc.lng();
          setMapMarker({ lat, lng });
          setMapCenter({ lat, lng });
          setSelectedAddress(results[0].formatted_address);
          mapRef.current?.panTo({ lat, lng });
          mapRef.current?.setZoom(14);
        }
      },
    );
  }, [mapSearchInput]);

  const confirmMapSelection = () => {
    if (mapMarker) {
      handleLocationSelect(mapMarker.lat, mapMarker.lng, selectedAddress);
    }
  };

  if (!isLoaded && !loadError) {
    return (
      <div className="nr-loader-full">
        <div className="nr-spinner"></div>
        <p>Loading Map Services...</p>
      </div>
    );
  }

  const searchDialogOpen = (NavbarData) => {
    setBlur(NavbarData);
  };

  return (
    <div className="nearby-restaurants">
      <Navbar handleNavbar={searchDialogOpen} />

      {/* Hero Section */}
      <section className="nr-hero">
        <div className="nr-hero__bg-wrapper">
          <img src={heroBanner} alt="Delicious Food" className="nr-hero__bg" />
          <div className="nr-hero__overlay"></div>
        </div>
        <div className="nr-hero__content">
          <h1 className="nr-hero__title">
            {searchMode === "location" ? (
              <>
                Discover restaurants <br />
                <span className="nr-hero__accent">near you</span>
              </>
            ) : (
              <>
                Find your favorite <br />
                <span className="nr-hero__accent">restaurants</span>
              </>
            )}
          </h1>
          <p className="nr-hero__subtitle">
            {searchMode === "location"
              ? "Curated restaurants within 5 miles of your location"
              : "Search by name to find specific spots"}
          </p>

          <div ref={searchContainerRef} className="nr-search-container-wrapper">
            <div className="nr-search-mode-toggle">
              <button
                className={`nr-mode-btn ${searchMode === "restaurant" ? "active" : ""}`}
                onClick={() => {
                  setSearchMode("restaurant");
                  setSuggestions([]);
                  setIsDropdownOpen(false);
                  setLocationQuery("");
                }}
              >
                <MdRestaurant size={16} />
                <span>By Name</span>
              </button>
              <button
                className={`nr-mode-btn ${searchMode === "location" ? "active" : ""}`}
                onClick={() => {
                  setSearchMode("location");
                  setSuggestions([]);
                  setIsDropdownOpen(false);
                  setLocationQuery("");
                }}
              >
                <MdLocationOn size={16} />
                <span>Near Me</span>
              </button>
            </div>

            <div className="nr-search-bar">
              <input
                type="text"
                placeholder={
                  searchMode === "location"
                    ? "Enter postcode, city, or area..."
                    : "Enter restaurant name..."
                }
                value={locationQuery}
                onChange={handleInputChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleManualSearch();
                  }
                }}
                onFocus={() => {
                  if (suggestions.length > 0) setIsDropdownOpen(true);
                }}
                className="nr-search-bar__input"
                aria-label="Search location"
                autoComplete="off"
              />
              <div className="nr-search-bar__actions">
                {searchMode === "location" && (
                  <>
                    <button
                      className="nr-btn-icon"
                      onClick={handleUseMyLocation}
                      disabled={loading}
                      title="Use my current location"
                    >
                      <MdMyLocation size={20} />
                    </button>
                    <button
                      className="nr-btn-icon"
                      onClick={() => setIsMapOpen(true)}
                      title="Pick on map"
                    >
                      <MdMap size={20} />
                    </button>
                  </>
                )}
                <button
                  className="nr-btn nr-btn--brand search-trigger"
                  onClick={handleManualSearch}
                  disabled={loading || !locationQuery.trim()}
                >
                  {loading ? (
                    <div
                      className="nr-spinner-sm"
                      style={{ width: 16, height: 16, borderColor: "white" }}
                    ></div>
                  ) : (
                    <span>Search</span>
                  )}
                </button>
              </div>
            </div>

            {isDropdownOpen && suggestions.length > 0 && (
              <div ref={dropdownRef} className="nr-autocomplete-dropdown">
                {suggestions.map((prediction) => (
                  <div
                    key={prediction.place_id || Math.random()}
                    className="nr-autocomplete-item"
                    onClick={() => handleSuggestionClick(prediction)}
                  >
                    {prediction.isBackendResult ? (
                      <>
                        {prediction.logoUrl ? (
                          <img
                            src={prediction.logoUrl}
                            alt=""
                            className="nr-suggestion-logo"
                          />
                        ) : (
                          <MdRestaurant className="nr-autocomplete-icon" />
                        )}
                        <div className="nr-suggestion-content">
                          <span className="nr-suggestion-main">
                            {prediction.description}
                          </span>
                          {prediction.structured_formatting?.secondary_text && (
                            <span className="nr-suggestion-sub">
                              {prediction.structured_formatting.secondary_text}
                            </span>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <MdPlace className="nr-autocomplete-icon" />
                        <div className="nr-suggestion-content">
                          <span className="nr-suggestion-main">
                            {prediction.description}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedAddress && hasSearched && searchMode === "location" && (
            <div className="nr-hero__status">
              <span className="nr-badge"></span>
              <span>{selectedAddress}</span>
            </div>
          )}
        </div>
      </section>

      {/* Results Section */}
      <section className="nr-results">
        <div className="nr-container">
          <div className="nr-results__header">
            <h2 className="nr-results__title">
              {loading ? (
                <span className="nr-pulse-text">Scanning...</span>
              ) : hasSearched ? (
                `${restaurants.length} ${searchMode === "restaurant" ? "Restaurants Found" : "Places Found"}`
              ) : (
                "Search to find nearby restaurants"
              )}
            </h2>
            {!loading &&
              selectedAddress &&
              hasSearched &&
              searchMode === "location" && (
                <p className="nr-results__subtitle">Within 5 miles</p>
              )}
          </div>

          {/* {error && (
            <div className="nr-alert nr-alert--error">
              <span className="nr-alert__icon">⚠️</span>
              <p>{error}</p>
              <button
                className="nr-btn nr-btn--sm nr-btn--outline"
                onClick={() => setError(null)}
              >
                Dismiss
              </button>
            </div>
          )} */}

          {loading && hasSearched ? (
            <div className="nr-grid nr-grid--loading">
              {[1, 2, 3, 4].map((n) => (
                <div key={n} className="nr-card nr-card--skeleton">
                  <div className="nr-card__image-wrapper nr-skeleton"></div>
                  <div className="nr-card__body">
                    <div className="nr-skeleton nr-skeleton--title"></div>
                    <div className="nr-skeleton nr-skeleton--text"></div>
                    <div className="nr-skeleton nr-skeleton--footer"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="nr-grid">
              {restaurants.length > 0
                ? restaurants.map((rest) => {
                    const images = Array.isArray(rest.backgroundImageUrl)
                      ? rest.backgroundImageUrl.filter(
                          (img) => img && img.trim() !== "",
                        )
                      : [];
                    const hasMultipleImages = images.length > 1;
                    const currentIdx = sliderIndices[rest.id] || 0;

                    return (
                      <div
                        className="nr-card-link"
                        key={rest.placeId}
                        onClick={() => handleCardClick(rest)}
                        style={{ cursor: "pointer" }}
                      >
                        <article className="nr-card">
                          <div className="nr-card__image-wrapper">
                            {images.length > 0 ? (
                              <>
                                <div
                                  className="nr-card__slider"
                                  style={{
                                    transform: `translateX(-${currentIdx * 100}%)`,
                                  }}
                                >
                                  {images.map((imgUrl, index) => (
                                    <div key={index} className="nr-card__slide">
                                      <img
                                        src={imgUrl.trim()}
                                        alt={`${rest.name} view ${index + 1}`}
                                        className="nr-card__image"
                                        loading="lazy"
                                        onError={(e) => {
                                          e.target.style.display = "none";
                                          if (index === images.length - 1)
                                            e.target.parentElement.innerHTML = `<div class="nr-card__placeholder">${rest.name.charAt(0)}</div>`;
                                        }}
                                      />
                                    </div>
                                  ))}
                                </div>
                                {hasMultipleImages && (
                                  <>
                                    {currentIdx > 0 && (
                                      <button
                                        className="nr-card__nav-btn nr-card__nav-btn--prev"
                                        onClick={(e) =>
                                          handleSlideChange(
                                            e,
                                            rest.id,
                                            images.length,
                                            "prev",
                                          )
                                        }
                                        aria-label="Previous image"
                                      >
                                        ‹
                                      </button>
                                    )}
                                    {currentIdx < images.length - 1 && (
                                      <button
                                        className="nr-card__nav-btn nr-card__nav-btn--next"
                                        onClick={(e) =>
                                          handleSlideChange(
                                            e,
                                            rest.id,
                                            images.length,
                                            "next",
                                          )
                                        }
                                        aria-label="Next image"
                                      >
                                        ›
                                      </button>
                                    )}
                                    <div className="nr-card__indicators">
                                      {images.map((_, idx) => (
                                        <span
                                          key={idx}
                                          className={`nr-card__indicator ${idx === currentIdx ? "nr-card__indicator--active" : ""}`}
                                        />
                                      ))}
                                    </div>
                                  </>
                                )}
                              </>
                            ) : rest.logoUrl ? (
                              <img
                                src={rest.logoUrl.trim()}
                                alt={rest.name}
                                className="nr-card__image"
                                loading="lazy"
                                style={{
                                  objectFit: "contain",
                                  padding: "20px",
                                }}
                              />
                            ) : (
                              <div className="nr-card__placeholder">
                                {rest.name.charAt(0)}
                              </div>
                            )}
                            <div className="nr-card__badges">
                              <span
                                className={`nr-status ${rest.isOpen ? "nr-status--open" : "nr-status--closed"}`}
                              >
                                {rest.isOpen ? "Open Now" : "Closed"}
                              </span>

                              {rest.distance && (
                                <span className="nr-status nr-status--distance">
                                  <MdDirectionsBike
                                    size={14}
                                    style={{ marginRight: "4px" }}
                                  />
                                  <span className="nr-distance-text">
                                    {rest.distance}
                                  </span>
                                </span>
                              )}

                              <div className="nrb-badge__new-line"></div>

                              {rest.isPetFriendly && (
                                <span className="nr-status nr-status--pet-friendly">
                                  <MdPets
                                    size={14}
                                    style={{ marginRight: "4px" }}
                                  />
                                  Pet Friendly
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="nr-card__body">
                            {rest.logoUrl ? (
                              <img
                                src={rest.logoUrl}
                                alt={rest.name}
                                className="nr-card__logo"
                              />
                            ) : (
                              <img
                                src="https://gotastelivestorage.blob.core.windows.net/gotasteimages/4f1389a4-f46b-4272-9594-a96513c83aad_Original        "
                                alt={rest.name}
                                className="nr-card__logo"
                              />
                            )}
                            <div className="nr-card__info">
                              <div className="nr-card__info-header">
                                <h3 className="nr-card__name">{rest.name}</h3>
                                <span className="nr-card__rating-text">
                                  ⭐4.5
                                </span>
                              </div>

                              <h4 className="nr-card__category">
                                {rest.category}
                              </h4>

                              <p className="nr-card__address">
                                {rest.vicinity}
                              </p>

                              <span className="nr-card__hours">
                                Opening Hour: 9:00 AM-10:00 PM
                              </span>
                            </div>
                          </div>
                        </article>
                      </div>
                    );
                  })
                : !error &&
                  hasSearched && (
                    <div className="nr-empty-state">
                      <div className="nr-empty-state__icon">🍽️</div>
                      <h3>No restaurants found</h3>
                      <p>Try adjusting your search term or location.</p>
                    </div>
                  )}
            </div>
          )}
        </div>
      </section>
      <Footer />

      {/* Map Modal (FIXED) */}
      {isMapOpen && (
        <div className="nr-modal-overlay" onClick={() => setIsMapOpen(false)}>
          <div
            className="nr-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            key="map-modal-container"
          >
            <div className="nr-modal__header">
              <h2>Select Exact Location</h2>
              <button
                className="nr-modal__close"
                onClick={() => setIsMapOpen(false)}
                aria-label="Close modal"
              >
                ✕
              </button>
            </div>
            <div className="nr-modal__search-bar">
              <input
                type="text"
                placeholder="Search city, street, or landmark..."
                value={mapSearchInput}
                onChange={(e) => setMapSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleMapSearch()}
                className="nr-modal__input"
                disabled={!isMapsReady}
              />
              <button
                className="nr-btn nr-btn--primary"
                onClick={handleMapSearch}
                disabled={!isMapsReady}
              >
                Search
              </button>
              <button
                className="nr-btn nr-btn--icon"
                onClick={() => {
                  if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition((pos) => {
                      const { latitude, longitude } = pos.coords;
                      setMapMarker({ lat: latitude, lng: longitude });
                      setMapCenter({ lat: latitude, lng: longitude });
                      reverseGeocode(latitude, longitude);
                    });
                  }
                }}
                disabled={!isMapsReady}
                title="Use current location"
              >
                <MdMyLocation size={20} />
              </button>
            </div>
            <div className="nr-modal__map-container">
              {isLoaded ? (
                <GoogleMap
                  // FIX: Force remount of the map instance when modal opens/closes
                  key={`map-${isMapOpen}-${mapCenter.lat}-${mapCenter.lng}`}
                  mapContainerStyle={{ width: "100%", height: "100%" }}
                  center={mapCenter}
                  zoom={14}
                  onClick={handleMapClick}
                  onLoad={onMapLoad}
                  options={{
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: false,
                    styles: [
                      {
                        featureType: "poi",
                        elementType: "labels",
                        stylers: [{ visibility: "off" }],
                      },
                    ],
                  }}
                >
                  {mapMarker && (
                    <Marker
                      // FIX: Explicitly key the marker to ensure it re-renders with new coords
                      key={`marker-${mapMarker.lat}-${mapMarker.lng}`}
                      position={mapMarker}
                      animation={window.google.maps.Animation.DROP}
                      draggable={true}
                      onDragEnd={handleMarkerDragEnd}
                    />
                  )}
                </GoogleMap>
              ) : (
                <div className="nr-map-loading">Loading Map...</div>
              )}
            </div>
            {selectedAddress && (
              <div className="nr-modal__selected-address">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#371b17"
                  strokeWidth="2"
                >
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                <span>{selectedAddress}</span>
              </div>
            )}
            <div className="nr-modal__actions">
              <button
                className="nr-btn nr-btn--ghost"
                onClick={() => setIsMapOpen(false)}
              >
                Cancel
              </button>
              <button
                className="nr-btn nr-btn--brand"
                onClick={confirmMapSelection}
                disabled={!mapMarker || loading}
              >
                {loading ? "Searching..." : "Find Restaurants Here"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isTableModalOpen && selectedRestaurant && (
        <div
          className="nr-modal-overlay"
          onClick={() => setIsTableModalOpen(false)}
          style={{ zIndex: 1100 }}
        >
          <div
            className="nr-modal nr-table-modal-split"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            style={{ maxWidth: "1200px", padding: 0, overflow: "hidden" }}
          >
            <button
              className="nr-modal__close nr-modal__close-absolute"
              onClick={() => setIsTableModalOpen(false)}
              aria-label="Close modal"
              style={{
                position: "absolute",
                top: "16px",
                right: "16px",
                zIndex: 20,
                background: "rgba(255,255,255,0.8)",
                borderRadius: "50%",
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MdClose size={20} />
            </button>

            <div
              style={{ display: "flex", flexDirection: "row", height: "100%" }}
            >
              {/* LEFT SIDE: Image Preview (Carousel) - 70% Width */}
              <div
                className="nr-table-preview-pane"
                style={{
                  flex: "7",
                  background: "#f3f4f6",
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "500px",
                  overflow: "hidden",
                }}
              >
                {tableLoading ? (
                  <div className="nr-spinner"></div>
                ) : selectedTable ? (
                  <>
                    {selectedTable.imageUrls &&
                    selectedTable.imageUrls.length > 0 ? (
                      <>
                        <img
                          src={selectedTable.imageUrls[tableImageIndex]}
                          alt={selectedTable.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            transition: "opacity 0.3s ease",
                          }}
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.nextSibling.style.display = "flex";
                          }}
                        />
                        <div
                          style={{
                            display: "none",
                            width: "100%",
                            height: "100%",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#ccc",
                            flexDirection: "column",
                            gap: "10px",
                          }}
                        >
                          <MdImageNotSupported size={64} />
                          <span>No Image Available</span>
                        </div>

                        {selectedTable.imageUrls.length > 1 && (
                          <>
                            <button
                              className="nr-carousel-btn nr-carousel-prev"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTableImageSlide("prev");
                              }}
                              style={{
                                position: "absolute",
                                left: "24px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                background: "rgba(255,255,255,0.8)",
                                border: "none",
                                borderRadius: "50%",
                                width: "48px",
                                height: "48px",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "1.8rem",
                                color: "#371b17",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                transition: "all 0.2s",
                              }}
                            >
                              <MdChevronLeft />
                            </button>
                            <button
                              className="nr-carousel-btn nr-carousel-next"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTableImageSlide("next");
                              }}
                              style={{
                                position: "absolute",
                                right: "24px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                background: "rgba(255,255,255,0.8)",
                                border: "none",
                                borderRadius: "50%",
                                width: "48px",
                                height: "48px",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "1.8rem",
                                color: "#371b17",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                transition: "all 0.2s",
                              }}
                            >
                              <MdChevronRight />
                            </button>

                            <div
                              style={{
                                position: "absolute",
                                bottom: "32px",
                                left: "50%",
                                transform: "translateX(-50%)",
                                display: "flex",
                                gap: "10px",
                                background: "rgba(0,0,0,0.6)",
                                padding: "8px 16px",
                                borderRadius: "24px",
                                backdropFilter: "blur(4px)",
                              }}
                            >
                              {selectedTable.imageUrls.map((_, idx) => (
                                <span
                                  key={idx}
                                  style={{
                                    width: "10px",
                                    height: "10px",
                                    borderRadius: "50%",
                                    background:
                                      idx === tableImageIndex
                                        ? "#fff"
                                        : "rgba(255,255,255,0.5)",
                                    transition: "all 0.2s",
                                    transform:
                                      idx === tableImageIndex
                                        ? "scale(1.2)"
                                        : "scale(1)",
                                  }}
                                />
                              ))}
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          color: "#9ca3af",
                        }}
                      >
                        <MdImageNotSupported
                          size={80}
                          style={{ marginBottom: "16px" }}
                        />
                        <h3 style={{ fontSize: "1.2rem" }}>
                          No Images Available
                        </h3>
                        <p>Preview for {selectedTable.name}</p>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ textAlign: "center", color: "#6b7280" }}>
                    <p>Select a table to view details</p>
                  </div>
                )}
              </div>

              {/* RIGHT SIDE: Table List - 30% Width */}
              <div
                className="nr-table-list-pane"
                style={{
                  flex: "3",
                  display: "flex",
                  flexDirection: "column",
                  background: "white",
                  borderLeft: "1px solid #e5e7eb",
                  maxHeight: "85vh",
                  minWidth: "280px",
                }}
              >
                <div
                  style={{
                    padding: "24px 20px 16px",
                    borderBottom: "1px solid #f3f4f6",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      marginBottom: "8px",
                    }}
                  >
                    {selectedRestaurant.logoUrl ? (
                      <img
                        src={selectedRestaurant.logoUrl}
                        alt=""
                        style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "50%",
                          background: "#f0f0f0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: "bold",
                        }}
                      >
                        {selectedRestaurant.name.charAt(0)}
                      </div>
                    )}
                    <h2
                      style={{
                        fontSize: "1.1rem",
                        margin: 0,
                        color: "#1a1a1a",
                        lineHeight: 1.2,
                      }}
                    >
                      Select a Table
                    </h2>
                  </div>
                </div>

                <div
                  style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}
                >
                  {tableLoading ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "40px",
                        color: "#6b7280",
                      }}
                    >
                      <div
                        className="nr-spinner"
                        style={{ margin: "0 auto 16px" }}
                      ></div>
                      <p>Loading tables...</p>
                    </div>
                  ) : tableError ? (
                    <div
                      className="nr-alert nr-alert--error"
                      style={{ margin: 0, fontSize: "0.85rem" }}
                    >
                      <span>⚠️</span>
                      <p>{tableError}</p>
                    </div>
                  ) : tableData.length === 0 ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "40px",
                        color: "#6b7280",
                      }}
                    >
                      <MdRestaurant
                        size={48}
                        style={{ marginBottom: "16px", opacity: 0.5 }}
                      />
                      <h3>No tables</h3>
                      <p style={{ fontSize: "0.9rem" }}>Contact restaurant.</p>
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                      }}
                    >
                      {tableData.map((table) => {
                        const isSelected = selectedTable?.id === table.id;
                        return (
                          <div
                            key={table.id}
                            className={`nr-table-list-item ${isSelected ? "nr-table-list-item--selected" : ""}`}
                            onClick={() => handleTableSelect(table)}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "12px",
                              borderRadius: "10px",
                              border: isSelected
                                ? "2px solid #371b17"
                                : "1px solid #e5e7eb",
                              background: isSelected ? "#fff5f4" : "white",
                              cursor: "pointer",
                              transition: "all 0.2s",
                              boxShadow: isSelected
                                ? "0 4px 12px rgba(55, 27, 23, 0.1)"
                                : "none",
                              transform: isSelected
                                ? "scale(1.02)"
                                : "scale(1)",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                overflow: "hidden",
                              }}
                            >
                              <div
                                style={{
                                  width: "50px",
                                  height: "50px",
                                  borderRadius: "6px",
                                  overflow: "hidden",
                                  background: "#f3f4f6",
                                  flexShrink: 0,
                                }}
                              >
                                {table.imageUrls &&
                                table.imageUrls.length > 0 ? (
                                  <img
                                    src={table.imageUrls[0]}
                                    alt=""
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                    }}
                                  />
                                ) : (
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: "#ccc",
                                    }}
                                  >
                                    <MdImageNotSupported size={20} />
                                  </div>
                                )}
                              </div>

                              <div style={{ overflow: "hidden" }}>
                                <h3
                                  style={{
                                    margin: "0 0 4px",
                                    fontSize: "0.95rem",
                                    fontWeight: "700",
                                    color: isSelected ? "#371b17" : "#1a1a1a",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {table.name}
                                </h3>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    fontSize: "0.8rem",
                                    color: "#6b7280",
                                  }}
                                >
                                  <span
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "3px",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    <MdPeople size={14} /> {table.capacity}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {isSelected && (
                              <div style={{ color: "#371b17", flexShrink: 0 }}>
                                <MdCheckCircle size={20} />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div
                  className="nr-modal__actions"
                  style={{
                    borderTop: "1px solid #f0f0f0",
                    backgroundColor: "white",
                    padding: "16px 20px",
                    justifyContent: "space-between",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.85rem",
                      color: "#6b7280",
                      lineHeight: 1.4,
                    }}
                  ></div>
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                    }}
                  >
                    <button
                      className="nr-btn nr-btn--ghost"
                      onClick={() => setIsTableModalOpen(false)}
                      style={{ padding: "8px 16px", fontSize: "0.9rem" }}
                    >
                      Cancel
                    </button>
                    <button
                      className="nr-btn nr-btn--brand"
                      onClick={handleConfirmBooking}
                      disabled={!selectedTable}
                      style={{
                        opacity: !selectedTable ? 0.5 : 1,
                        padding: "8px 20px",
                        fontSize: "0.9rem",
                      }}
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NearByRestaurants;
